import { useCallback, useRef, useState } from 'react'
import AlertsService from '../../../services/api/AlertsService'
import { showToast } from '../../shared/Toast'

const DUPLICATE_SUCCESS_MSG = 'Alerte dupliquée avec succès.'
const DELETE_SUCCESS_MSG = 'Alerte supprimée avec succès.'
const ERROR_MSG = 'Une erreur est survenue. Veuillez réessayer ou contacter le support '
  + 'si le problème persiste.'

const noop = () => null

const useAlertMutations = (collectId, callbacks = {}) => {
  const { onSuccess = noop, onError = noop, toastContainerId = null } = callbacks
  const [mutatingIds, setMutatingIds] = useState(() => new Set())
  // Keep latest callbacks in a ref so the returned functions stay stable.
  const callbacksRef = useRef({ onSuccess, onError, toastContainerId })
  callbacksRef.current = { onSuccess, onError, toastContainerId }

  const markMutating = useCallback((alertId, isMutating) => {
    setMutatingIds(previous => {
      const next = new Set(previous)
      if (isMutating)
        next.add(alertId)
      else
        next.delete(alertId)
      return next
    })
  }, [])

  const duplicate = useCallback(async alertId => {
    const refs = callbacksRef.current
    markMutating(alertId, true)
    try {
      const response = await AlertsService.duplicateAlert(collectId, alertId)
      refs.onSuccess({ action: 'duplicate', alertId, response })
      showToast(DUPLICATE_SUCCESS_MSG, 'success', refs.toastContainerId)
      return { ok: true, response }
    } catch (error) {
      refs.onError({ action: 'duplicate', alertId, error })
      showToast(ERROR_MSG, 'error', refs.toastContainerId)
      return { ok: false, error }
    } finally {
      markMutating(alertId, false)
    }
  }, [collectId, markMutating])

  const destroy = useCallback(async alertId => {
    const refs = callbacksRef.current
    markMutating(alertId, true)
    try {
      await AlertsService.destroyAlert(collectId, alertId)
      refs.onSuccess({ action: 'destroy', alertId })
      showToast(DELETE_SUCCESS_MSG, 'success', refs.toastContainerId)
      return { ok: true }
    } catch (error) {
      refs.onError({ action: 'destroy', alertId, error })
      showToast(ERROR_MSG, 'error', refs.toastContainerId)
      return { ok: false, error }
    } finally {
      markMutating(alertId, false)
    }
  }, [collectId, markMutating])

  const isMutating = useCallback(alertId => mutatingIds.has(alertId), [mutatingIds])

  return { duplicate, destroy, isMutating, mutatingIds }
}

export default useAlertMutations
